import { useState, useEffect } from "react";
import Pagination from "../../../components/Pagination/Pagination";
import ErrorMessage from "../../../components/ErrorMessage/ErrorMessage";
import {
  ReportActionButtons,
  SECTION_KEYS,
} from "../components/ReportActionButtons";
import {
  convertISOToReadableDate,
  getDuplicateSubmissionById,
  showErrorToast,
} from "../../../helpers";
import {
  handleSetTriggerOrMatchStatus,
  handleSetSubmissionStatus,
} from "../../../helpers/statusHelpers";
import { SignalReportModal, EmptyInbox } from "../../../components";
import ConfirmationDialog from "../../../components/ConfirmationDialog/ConfirmationDialog";

import {
  ApplyAction,
  ColorizeStatusText,
} from "../../../components/ApplyAction";

import { useAppContext } from "../../../components/AppContext";
import useAmbientScreenedManuscripts from "../hooks/useAmbientScreenedManuscripts";
import { ambientScreeenedColumns as baseColumns } from "../columns";
import { dropdownOptions } from "../dropdownOptions";
import { DataTable } from "stm-frontend-library";

const STATUS_MAPPINGS = {
  default: {
    "signal confirmed": "confirmed",
  },
};

const mapSubmissionStatus = (status, type) => {
  const mappings = STATUS_MAPPINGS[type] || STATUS_MAPPINGS.default || {};
  return mappings[status] || status;
};

export const AmbientScreenedManuscriptsTab = ({
  isActiveTab,
  type,
  showActionColumn,
}) => {
  const [selectedItem, setSelectedItem] = useState({
    index: null,
    visibleSection: SECTION_KEYS.ALL,
  });

  const [fetchedData, setFetchedData] = useState(null);
  const [isFetchingDupSubData, setIsFetchingDupSubData] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    actionType: null,
    actionContext: null,
  });
  const { searchInput } = useAppContext();

  const {
    data,
    fetchData,
    setData,
    error,
    pageCount,
    currentPage,
    setCurrentPage,
    searchTerm,
    setSearchTerm,
    isLoading,
  } = useAmbientScreenedManuscripts({ isActiveTab, type });

  const handleIconClick = (index, buttonKey) => {
    setSelectedItem({
      index,
      visibleSection: buttonKey,
    });
  };

  const isInboxTabSelected = type === "wo_status";

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  const ACTIONS_REQUIRING_CONFIRMATION = ["suspect paper mill"];

  const updateStatus = async (index, status, additionalParams = {}) => {
    const newData = [...data];

    if (isInboxTabSelected) {
      const previousElement = { ...newData[index] };
      newData.splice(index, 1);
      setData(newData);

      const { id: recordId } = previousElement;
      try {
        await handleSetSubmissionStatus(recordId, status, additionalParams);
        await fetchData();
      } catch {
        showErrorToast();
        newData.splice(index, 0, previousElement);
        setData(newData);
      }
    } else if (type === "w_signals") {
      const previousStatus = newData[index].submission_status;
      newData[index].submission_status = status;

      const { id: recordId } = newData[index];
      try {
        await handleSetSubmissionStatus(recordId, status, additionalParams);
      } catch {
        showErrorToast();
        newData[index].submission_status = previousStatus;
      } finally {
        setData(newData);
      }
    }
  };

  const onActionSelect = (index, status) => {
    if (ACTIONS_REQUIRING_CONFIRMATION.includes(status)) {
      setConfirmationDialog({
        isOpen: true,
        actionType: status,
        actionContext: { index, value: status },
      });
      return;
    }
    updateStatus(index, status);
  };

  const handleConfirmationApply = (options) => {
    const { index, value } = confirmationDialog.actionContext;
    const item = data[index];

    const fullParams = {
      ...(options.addToSignalcheck && {
        signalCheck: {
          user: { comment: options.comment || "" },
          manuscript: {
            title: item.title,
            manuscript_id: item.manuscript_id,
            publisher_id: item.publisher_id,
            user_id: item.user_id,
          },
        },
      }),
    };

    updateStatus(index, value, fullParams);
    setConfirmationDialog({ isOpen: false, actionType: null, actionContext: null });
  };

  const handleConfirmationClose = () => {
    setConfirmationDialog({ isOpen: false, actionType: null, actionContext: null });
  };

  const onActionUndo = (index) => {
    updateStatus(index, null);
  };

  const fetchDupSubDataIfNeeded = async (index) => {
    const { persistent_id, signals } = data[index];
    if (persistent_id && signals?.some((s) => s === "duplicateSubmissions")) {
      try {
        setIsFetchingDupSubData(true);
        const { data: fetchedData } = await getDuplicateSubmissionById(
          persistent_id
        );
        setFetchedData(fetchedData);
      } finally {
        setIsFetchingDupSubData(false);
      }
    }
  };

  useEffect(() => {
    if (selectedItem.index !== null) {
      fetchDupSubDataIfNeeded(selectedItem.index);
    } else {
      setFetchedData(null);
    }
  }, [selectedItem, data]);

  const resetModalState = () => {
    setFetchedData(null);
    setSelectedItem({
      index: null,
      visibleSection: SECTION_KEYS.ALL,
    });
  };

  useEffect(() => {
    const trimmedValue = searchInput.trim();
    if ((!searchTerm && !trimmedValue) || trimmedValue === searchTerm) return;
    setSearchTerm(trimmedValue);
  }, [searchInput, searchTerm, setSearchTerm]);

  const tableColumns = showActionColumn
    ? baseColumns.filter((column) => column.field !== "submission_status")
    : baseColumns.filter((column) => column.field !== "action");

  const dataWithActions = data?.map((manuscript, index) => {
    const row = {
      ...manuscript,
      timestamp: convertISOToReadableDate(manuscript.timestamp, "DD MMM YYYY"),
      signals: (
        <ReportActionButtons
          signals={manuscript.signals}
          onIconClick={(buttonKey) => handleIconClick(index, buttonKey)}
        />
      ),
      submission_status: (
        <ColorizeStatusText
          statusText={mapSubmissionStatus(manuscript.submission_status)}
        />
      ),
    };

    if (showActionColumn) {
      row.action = (
        <div className="d-flex">
          <ApplyAction
            index={index}
            status={mapSubmissionStatus(manuscript.submission_status)}
            selectAction={onActionSelect}
            undoAction={onActionUndo}
            options={dropdownOptions}
          />
        </div>
      );
    }

    return row;
  });

  return (
    <>
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        actionType={confirmationDialog.actionType}
        onClose={handleConfirmationClose}
        onApply={handleConfirmationApply}
      />
      {selectedItem.index !== null && (
        <SignalReportModal
          dupSubData={fetchedData}
          contentData={data[selectedItem.index]}
          visibleSection={selectedItem.visibleSection}
          onClose={resetModalState}
          setTriggerOrMatchStatus={handleSetTriggerOrMatchStatus}
          setSubmissionStatus={handleSetSubmissionStatus}
          fetchData={fetchData}
        />
      )}
      {error && <ErrorMessage />}
      {!error && (
        <>
          <DataTable
            columns={tableColumns}
            rows={dataWithActions}
            customNoDataComponent={isInboxTabSelected ? EmptyInbox : null}
            isLoading={isLoading}
            loadingMode="overlay"
          />
          <Pagination
            handlePageClick={handlePageClick}
            pageCount={pageCount}
            currentPage={currentPage}
            disabled={isLoading}
          />
        </>
      )}
    </>
  );
};

export default AmbientScreenedManuscriptsTab;
